import { useMemo, useState } from "react"
import { Autocomplete, Divider, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { BoundCheckBox } from "lib/@components/standard-checkbox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If } from "lib/@components/switch"
import { getClientGroups } from "routes/schedule/my-schedules/my-custom-schedules/controller/get-client-trees"
import { assetRegisters } from "routes/asset-register/controller/asset-registers"
import { ruleSets } from "routes/rule-sets/controller/rule-sets"
import { useUniqueNameValidator } from "../use-unique-name-validation"

export function CreateRuleSetFormBody({ currentRuleSetId }) {
    const { target, refresh } = useBoundContext()
    const clientGroups = getClientGroups.useResults()
    const regimeOptions = useMemo(buildRegimeOptions, [clientGroups])

    const registerResult = assetRegisters.useResults({ skip: 0, take: 100 })
    const registerOptions = useMemo(getRegisterOptions, [registerResult])

    // status:"all" so the duplicate-name check matches the server (which counts archived too).
    const ruleSetResult = ruleSets.useResults({ skip: 0, take: 1000, status: "all" })
    const validateName = useUniqueNameValidator(ruleSetResult?.items, "rule set", { excludeId: currentRuleSetId })

    const [contextMode, setContextMode] = useState(target.assetRegister && !target.regime ? "assetRegister" : "regime")

    return (
        <Stack spacing={2.5} sx={{ mt: 1 }}>
            <BoundTextField field="name" label="Name" yup={validateName} autoFocus />
            <BoundTextField field="description" label="Description" multiline minRows={3} />
            <Stack spacing={1.25}>
                <ToggleButtonGroup
                    value={contextMode}
                    exclusive
                    onChange={handleModeChange}
                    size="small"
                    sx={{ alignSelf: "flex-start" }}
                >
                    <ToggleButton value="regime" sx={{ textTransform: "none", px: 2 }}>
                        Regime
                    </ToggleButton>
                    <ToggleButton value="assetRegister" sx={{ textTransform: "none", px: 2 }}>
                        Asset Register
                    </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body2" color="text.secondary">
                    Pick a regime or asset register so that you can create rules from it&apos;s information
                </Typography>
                <If value={contextMode === "regime"}>
                    <Autocomplete
                        options={regimeOptions}
                        getOptionLabel={getOptionLabel}
                        isOptionEqualToValue={isOptionEqual}
                        value={target.regime ?? null}
                        onChange={handleRegimeChange}
                        renderInput={renderRegimeInput}
                        fullWidth
                    />
                </If>
                <If value={contextMode === "assetRegister"}>
                    <Autocomplete
                        options={registerOptions}
                        getOptionLabel={getOptionLabel}
                        isOptionEqualToValue={isOptionEqual}
                        value={target.assetRegister ?? null}
                        onChange={handleRegisterChange}
                        renderInput={renderRegisterInput}
                        fullWidth
                    />
                </If>
            </Stack>
            <Divider />
            <Stack spacing={1.5}>
                <BoundCheckBox
                    field="includeByDefault"
                    label="Include all tasks by default"
                    labelRight
                    sx={{ flex: "0 0 auto", alignSelf: "flex-start" }}
                />
                <Typography variant="body2" color="text.secondary">
                    When ticked, all tasks start included and rules can exclude specific ones. Untick to start with
                    everything excluded and use rules to include what you need.
                </Typography>
            </Stack>
        </Stack>
    )

    function buildRegimeOptions() {
        return (clientGroups ?? []).flatMap((g) => {
            const entries = []
            if (g.hasLive) {
                entries.push({ id: `live|${g._id}`, label: g.name })
            }
            if (g.hasReady && !g.hasLive) {
                entries.push({ id: `ready|${g._id}`, label: `${g.name} (Ready)` })
            }
            if (!g.hasLive && !g.hasReady) {
                entries.push({ id: g._id, label: g.name })
            }
            return entries
        })
    }

    function getRegisterOptions() {
        return (registerResult?.items ?? []).map((r) => ({ id: r.id, label: r.name }))
    }

    function handleModeChange(_, value) {
        if (!value) return
        setContextMode(value)
        if (value === "regime") {
            target.assetRegister = null
        } else {
            target.regime = null
        }
        refresh()
    }

    function handleRegimeChange(_, value) {
        target.regime = value
        refresh()
    }

    function handleRegisterChange(_, value) {
        target.assetRegister = value
        refresh()
    }
}

function getOptionLabel(option) {
    return option?.label || ""
}

function isOptionEqual(option, value) {
    return option?.id === value?.id
}

function renderRegimeInput(params) {
    return <TextField {...params} label="Regime (Optional)" placeholder="Select a regime..." />
}

function renderRegisterInput(params) {
    return <TextField {...params} label="Asset (Optional)" placeholder="Select an asset register..." />
}
